import { useMemo, useState, type ReactNode } from "react";
import { countyLabel, countyNoun, countyNounForCount } from "@/lib/county-label";
import { Link } from "@tanstack/react-router";
import { Search } from "lucide-react";
import { cn } from "@/lib/utils";

/** Below this, the whole list is shorter than a search box plus its results. */
const SHOW_ALL_UNDER = 24;
const MAX_MATCHES = 12;

/**
 * Type-ahead for a state's county pages. Texas has 254 counties; as a plain grid that was
 * three phone screens of names between the prevention checklist and the FAQ.
 *
 * Every link stays in the server-rendered HTML (inside a closed <details>) so the county
 * pages remain crawlable and work without JavaScript.
 */
export function CountyFinder({
  stateName,
  stateSlug,
  stateAbbr,
  counties,
  map,
}: {
  stateName: string;
  stateSlug: string;
  stateAbbr: string;
  counties: Array<{ name: string; slug: string; fips: string }>;
  /** A map of the state's counties, set beside the search on wide screens. */
  map?: ReactNode;
}) {
  const noun = countyNoun(stateAbbr);
  const nouns = countyNoun(stateAbbr, true);
  const [query, setQuery] = useState("");
  const q = query
    .trim()
    .toLowerCase()
    .replace(/\s+(county|parish)$/, "");
  const matches = useMemo(
    () =>
      q
        ? counties
            .filter((c) => c.name.toLowerCase().includes(q))
            .sort(
              (a, b) =>
                Number(!a.name.toLowerCase().startsWith(q)) -
                Number(!b.name.toLowerCase().startsWith(q)),
            )
            .slice(0, MAX_MATCHES)
        : [],
    [counties, q],
  );

  const grid = (
    <div className="grid grid-cols-2 gap-x-4 gap-y-1 sm:grid-cols-3 lg:grid-cols-4">
      {counties.map((c) => (
        <CountyLink key={c.slug} stateSlug={stateSlug} stateAbbr={stateAbbr} county={c} />
      ))}
    </div>
  );

  return (
    <section aria-labelledby="county-finder" className="no-print border-t border-rule/70 pt-6">
      <h2
        id="county-finder"
        className="font-display text-[1.75rem] font-medium leading-tight text-foreground"
      >
        Find your {noun}
      </h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Each of {stateName}&rsquo;s {counties.length} {countyNounForCount(stateAbbr)} has its own
        page with CDC&rsquo;s record for it, a map of the {nouns} around it, and season and
        prevention notes.
      </p>

      <div
        className={cn(
          map && "lg:grid lg:grid-cols-[minmax(0,1fr)_24rem] lg:items-start lg:gap-x-10",
        )}
      >
        <div className="min-w-0">
          {counties.length < SHOW_ALL_UNDER ? (
            <div className="mt-4">{grid}</div>
          ) : (
            <>
              <label htmlFor="county-finder-input" className="sr-only">
                {noun === "parish" ? "Parish" : "County"} name
              </label>
              <div className="relative mt-4 max-w-md">
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  aria-hidden
                />
                <input
                  id="county-finder-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={`Type a ${noun}, e.g. ${counties[0]?.name ?? ""}`}
                  autoComplete="off"
                  className="w-full rounded-sm border border-input bg-card py-3 pl-9 pr-3 text-base outline-none focus:border-primary focus:ring-2 focus:ring-primary/25 sm:text-sm"
                />
              </div>
              {q && (
                <div aria-live="polite" className="mt-3">
                  {matches.length ? (
                    <ul className="grid gap-1 sm:grid-cols-2">
                      {matches.map((c) => (
                        <li key={c.slug}>
                          <CountyLink
                            stateSlug={stateSlug}
                            stateAbbr={stateAbbr}
                            county={c}
                            prominent
                          />
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      No {stateName} {noun} matches &ldquo;{query}&rdquo;.
                    </p>
                  )}
                </div>
              )}
              <details className="group mt-4">
                <summary className="cursor-pointer text-sm font-semibold text-primary hover:underline">
                  <span className="group-open:hidden">
                    Browse all {counties.length} {nouns}
                  </span>
                  <span className="hidden group-open:inline">Hide the list</span>
                </summary>
                <div className="mt-3">{grid}</div>
              </details>
            </>
          )}
        </div>
        {map && <div className="mt-8 lg:mt-4">{map}</div>}
      </div>
    </section>
  );
}

function CountyLink({
  stateSlug,
  stateAbbr,
  county,
  prominent = false,
}: {
  stateSlug: string;
  stateAbbr: string;
  county: { name: string; slug: string; fips: string };
  prominent?: boolean;
}) {
  return (
    <Link
      to="/states/$state/$county"
      params={{ state: stateSlug, county: county.slug }}
      className={cn(
        "block truncate rounded px-1 py-0.5 text-sm hover:text-primary hover:underline",
        prominent ? "py-1.5 font-medium text-foreground" : "text-muted-foreground",
      )}
    >
      {countyLabel({ ...county, stateAbbr })}
    </Link>
  );
}
